/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Image as ImageIcon, Music, ClipboardCheck, MessageSquare, ArrowRight, FileSignature, CreditCard, ClipboardList, ListMusic, CalendarClock, Palette, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { meApi, formatApiError } from "@/lib/api";
import { formatDateTimeLong } from "@/lib/date";

const KIND_LABEL = {
  photobooth_template: "Template photobooth",
  audio_maquette: "Maquette audio",
};

const STATUS_LABEL = {
  pending: "En attente",
  approved: "Approuvé",
  rejected: "À revoir",
};

const PENDING_ICONS = {
  sign_devis: FileSignature,
  pay_deposit: CreditCard,
  pay_invoice: CreditCard,
  questionnaire_prep: ClipboardList,
  playlist: ListMusic,
  rdv_pending: CalendarClock,
  photobooth_template: Palette,
  audio_maquette: AudioLines,
};

function fmtDate(iso) {
  return iso ? (formatDateTimeLong(iso) || "—") : "—";
}

/**
 * Espace Client — Validations à approuver (template photobooth, maquette audio).
 */
export default function Validations() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileBlobs, setFileBlobs] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      meApi.validations().catch(() => []),
      meApi.pendingActions().catch(() => ({ items: [] })),
    ])
      .then(([vals, pa]) => {
        setItems(vals || []);
        setPending((pa && pa.items) || []);
        if (vals && vals.length && !selected) setSelected(vals[0]);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadFile = async (item) => {
    if (fileBlobs[item.id]) return;
    const token = localStorage.getItem("ma_token");
    try {
      const r = await fetch(meApi.validationFileUrl(item.id), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setFileBlobs((s) => ({ ...s, [item.id]: url }));
    } catch {
      toast.error("Impossible de charger le fichier.");
    }
  };
  useEffect(() => {
    if (selected) loadFile(selected);
    // eslint-disable-next-line
  }, [selected]);

  const decide = async (approved) => {
    if (!selected) return;
    setBusy(true);
    try {
      await meApi.submitValidation(selected.id, { approved, comment });
      toast.success(approved ? "Merci pour votre validation !" : "Retours transmis. Nous vous préparons une nouvelle version.");
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const pendingValidations = items.filter((v) => v.status === "pending");
  const decided = items.filter((v) => v.status !== "pending");
  // "Other" pending items = everything from the aggregator that is NOT already
  // shown as a full validation card (photobooth_template & audio_maquette).
  const otherPending = pending.filter((p) => !["photobooth_template", "audio_maquette"].includes(p.kind));

  return (
    <div data-testid="ec-validations">
      <header className="mb-8">
        <p className="eyebrow">Espace client</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          À <span className="italic gold-text">valider.</span>
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl">
          Toutes les actions qui vous attendent — documents, paiements, questionnaire, playlist, validations créatives.
        </p>
      </header>

      {/* ============ Global pending actions ============ */}
      {!loading && otherPending.length > 0 && (
        <section className="mb-10" data-testid="ec-pending-actions">
          <div className="flex items-baseline justify-between mb-4">
            <p className="eyebrow text-[#DCB457]">Actions en attente</p>
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/40">{otherPending.length} tâche{otherPending.length > 1 ? "s" : ""}</span>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ec-pending-list">
            {otherPending.map((p) => {
              const Icon = PENDING_ICONS[p.kind] || ClipboardCheck;
              return (
                <li key={`${p.kind}-${p.id || p.label}`}>
                  <Link
                    to={p.link || "#"}
                    data-testid={`pending-action-${p.kind}`}
                    className="card-luxe p-4 flex items-start gap-3 hover:border-[#DCB457]/60 transition group"
                  >
                    <Icon size={18} strokeWidth={1.4} className="text-[#DCB457] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-serif-luxe text-white leading-tight">{p.label}</p>
                      {p.detail && <p className="text-[11px] text-white/55 mt-1 leading-snug">{p.detail}</p>}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] group-hover:translate-x-0.5 transition inline-flex items-center gap-1 shrink-0 self-center">
                      {p.cta || "Ouvrir"} <ArrowRight size={11} strokeWidth={1.6} />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {loading ? (
        <p className="text-white/55 italic flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 && otherPending.length === 0 ? (
        <div className="card-luxe p-12 text-center" data-testid="validations-empty">
          <ClipboardCheck size={40} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
          <p className="text-white/65 mb-2">Tout est à jour, aucune action en attente pour le moment.</p>
          <p className="text-white/45 text-sm">Dès qu'un template, une maquette ou un document sera prêt, vous serez notifié par email.</p>
        </div>
      ) : items.length === 0 ? null : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar list */}
          <aside className="lg:col-span-1 space-y-3" data-testid="validations-list">
            {pendingValidations.length > 0 && (
              <>
                <p className="eyebrow text-[#DCB457]">À valider ({pendingValidations.length})</p>
                {pendingValidations.map((v) => <ItemRow key={v.id} v={v} active={selected?.id === v.id} onClick={() => setSelected(v)} />)}
              </>
            )}
            {decided.length > 0 && (
              <>
                <p className="eyebrow text-white/50 mt-6">Historique ({decided.length})</p>
                {decided.map((v) => <ItemRow key={v.id} v={v} active={selected?.id === v.id} onClick={() => setSelected(v)} />)}
              </>
            )}
          </aside>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selected && (
              <div className="card-luxe p-6 md:p-8" data-testid={`validation-detail-${selected.id}`}>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <p className="eyebrow">{KIND_LABEL[selected.kind]}</p>
                    <h2 className="font-serif-luxe text-2xl md:text-3xl mt-1.5">{selected.label || KIND_LABEL[selected.kind]}</h2>
                    <p className="text-white/50 text-xs mt-1">Déposé le {fmtDate(selected.created_at)}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.16em] border px-2.5 py-1 ${
                    selected.status === "approved" ? "text-emerald-300 border-emerald-400/50 bg-emerald-500/10" :
                    selected.status === "rejected" ? "text-red-300 border-red-400/50 bg-red-500/10" :
                    "text-[#DCB457] border-[#DCB457]/50 bg-[#DCB457]/10"
                  }`}>
                    {STATUS_LABEL[selected.status] || selected.status}
                  </span>
                </div>

                {/* Preview */}
                <div className="bg-black/40 border border-[#DCB457]/15 p-4 mb-6" data-testid="validation-preview">
                  {fileBlobs[selected.id] ? (
                    selected.kind === "audio_maquette" ? (
                      <audio controls className="w-full" data-testid="validation-audio-player">
                        <source src={fileBlobs[selected.id]} type={selected.content_type} />
                        Votre navigateur ne supporte pas l'audio.
                      </audio>
                    ) : selected.content_type === "application/pdf" ? (
                      <iframe
                        src={fileBlobs[selected.id]}
                        title="Template"
                        className="w-full h-[500px] bg-white"
                        data-testid="validation-pdf-viewer"
                      />
                    ) : (
                      <img
                        src={fileBlobs[selected.id]}
                        alt="Template"
                        className="max-h-[500px] mx-auto"
                        data-testid="validation-image-viewer"
                      />
                    )
                  ) : (
                    <p className="text-white/50 flex items-center gap-2 py-8 justify-center">
                      <Loader2 size={14} className="animate-spin" /> Chargement…
                    </p>
                  )}
                </div>

                {selected.status === "pending" ? (
                  <div className="space-y-4">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-white/55 flex items-center gap-1.5 mb-2">
                        <MessageSquare size={11} strokeWidth={1.5} /> Un commentaire (facultatif)
                      </span>
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        data-testid="validation-comment"
                        placeholder="Par exemple : « J'aimerais changer la couleur… »"
                        className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm text-white focus:border-[#DCB457] outline-none"
                      />
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => decide(true)}
                        disabled={busy}
                        data-testid="validation-approve"
                        className="btn-gold inline-flex items-center gap-2 !py-3 disabled:opacity-60"
                      >
                        <CheckCircle2 size={14} strokeWidth={1.5} /> J'approuve
                      </button>
                      <button
                        onClick={() => decide(false)}
                        disabled={busy}
                        data-testid="validation-reject"
                        className="btn-ghost-gold inline-flex items-center gap-2 !text-red-300 !border-red-400/40 hover:!bg-red-500/10 !py-3 disabled:opacity-60"
                      >
                        <XCircle size={14} strokeWidth={1.5} /> Demander une nouvelle version
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-l-2 border-[#DCB457] pl-4 py-2">
                    <p className="text-xs text-white/50 mb-1">Décision prise le {fmtDate(selected.decided_at)}</p>
                    {selected.client_comment && <p className="text-white/75 text-sm italic">« {selected.client_comment} »</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({ v, active, onClick }) {
  const Icon = v.kind === "audio_maquette" ? Music : ImageIcon;
  return (
    <button
      onClick={onClick}
      data-testid={`validation-row-${v.id}`}
      className={`w-full text-left card-luxe p-3 transition hover:border-[#DCB457]/40 ${active ? "border-[#DCB457]/60 bg-[#DCB457]/5" : ""}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={16} strokeWidth={1.4} className="text-[#DCB457] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-serif-luxe leading-tight truncate">{v.label || KIND_LABEL[v.kind]}</p>
          <p className="text-[10px] uppercase tracking-[0.14em] text-white/45 mt-0.5">
            {v.status === "pending" ? "À valider" : STATUS_LABEL[v.status]}
          </p>
        </div>
      </div>
    </button>
  );
}
